export interface Package {
  id: string;
  name: string;
  price: string;
  priceNumber: number;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}

export interface CartItem {
  id: string;
  name: string;
  price: string;
  priceNumber: number;
  period: string;
  quantity: number;
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  avatar: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export const NAV_LINKS = [
  { href: "/#hero", anchor: "#hero", label: "Inicio" },
  { href: "/#agents", anchor: "#agents", label: "Agentes" },
  { href: "/#pricing", anchor: "#pricing", label: "Precios" },
  { href: "/#testimonials", anchor: "#testimonials", label: "Testimonios" },
  { href: "/#faq", anchor: "#faq", label: "FAQ" },
  { href: "/#contact", anchor: "#contact", label: "Contacto" },
];

export const PACKAGES: Package[] = [
  {
    id: "basic",
    name: "SofIA Básica",
    price: "$249",
    priceNumber: 249,
    period: "/mes",
    description: "Agente IA básico para uso personal",
    features: [
      "Asistente personal 24/7",
      "Gestión de tareas diarias",
      "Recordatorios inteligentes",
      "Integración con calendario",
      "Soporte por email",
    ],
  },
  {
    id: "pro",
    name: "SofIA Pro",
    price: "$559",
    priceNumber: 559,
    period: "/mes",
    description: "Agente IA avanzado para profesionales",
    features: [
      "Todo lo de SofIA Básica",
      "Análisis de productividad",
      "Automatización de workflows",
      "Integración con herramientas profesionales",
      "Reportes personalizados",
      "Soporte prioritario",
    ],
    highlighted: true,
  },
  {
    id: "enterprise",
    name: "SofIA Empresarial",
    price: "$1,499",
    priceNumber: 1499,
    period: "/mes",
    description: "Solución IA completa para empresas",
    features: [
      "Todo lo de SofIA Pro",
      "Múltiples agentes especializados",
      "Integración empresarial completa",
      "Dashboard de administración",
      "Análisis avanzado de datos",
      "Soporte dedicado 24/7",
      "Personalización completa",
    ],
  },
];

export const AGENTS: Agent[] = [
  {
    id: "personal",
    name: "Asistente Personal SofIA",
    description: "Tu compañero inteligente para organizar tu vida diaria, gestionar tareas y recordatorios.",
    icon: "user-circle",
  },
  {
    id: "work",
    name: "SofIA Trabajo",
    description: "Optimiza tu productividad laboral con automatización de tareas y gestión de proyectos.",
    icon: "briefcase",
  },
  {
    id: "health",
    name: "SofIA Salud",
    description: "Monitorea tu bienestar, recibe recordatorios de medicamentos y consejos de salud personalizados.",
    icon: "heart-pulse",
  },
  {
    id: "finance",
    name: "SofIA Finanzas",
    description: "Gestiona tus finanzas personales, presupuestos y recibe análisis de gastos inteligentes.",
    icon: "wallet",
  },
  {
    id: "home",
    name: "SofIA Hogar",
    description: "Controla tu hogar inteligente, automatiza rutinas y optimiza el consumo energético.",
    icon: "home",
  },
  {
    id: "education",
    name: "SofIA Educación",
    description: "Asistente de aprendizaje personalizado con planes de estudio adaptativos y seguimiento de progreso.",
    icon: "graduation-cap",
  },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    id: "1",
    name: "María González",
    role: "Directora de Marketing",
    content: "SofIA Pro ha transformado completamente mi productividad. Ahora puedo enfocarme en lo que realmente importa mientras la IA maneja las tareas repetitivas.",
    avatar: "MG",
  },
  {
    id: "2",
    name: "Carlos Rodríguez",
    role: "Emprendedor",
    content: "La integración de múltiples agentes en SofIA Empresarial nos ha permitido escalar nuestro negocio sin aumentar el equipo. Increíble ROI.",
    avatar: "CR",
  },
  {
    id: "3",
    name: "Ana Martínez",
    role: "Profesional Independiente",
    content: "Como freelancer, SofIA Básica es mi asistente perfecto. Gestiona mi agenda, recordatorios y me ayuda a mantener el equilibrio vida-trabajo.",
    avatar: "AM",
  },
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: "1",
    question: "¿Qué es SofIA?",
    answer: "SofIA es una plataforma que ofrece agentes de inteligencia artificial especializados para diferentes aspectos de tu vida diaria. Desde gestión personal hasta automatización empresarial, nuestros agentes están diseñados para hacer tu vida más fácil y productiva.",
  },
  {
    id: "2",
    question: "¿Cómo funcionan los agentes de IA?",
    answer: "Nuestros agentes utilizan tecnología de IA avanzada para aprender de tus patrones y preferencias. Se integran con tus herramientas existentes y trabajan de forma autónoma para completar tareas, proporcionar insights y automatizar procesos según tus necesidades.",
  },
  {
    id: "3",
    question: "¿Puedo cambiar de plan en cualquier momento?",
    answer: "Sí, puedes actualizar o cambiar tu plan en cualquier momento. Los cambios se reflejarán en tu próximo ciclo de facturación y no hay penalizaciones por cambiar de plan.",
  },
  {
    id: "4",
    question: "¿Mis datos están seguros?",
    answer: "Absolutamente. Utilizamos encriptación de nivel empresarial y cumplimos con todas las regulaciones de privacidad de datos. Tus datos nunca se comparten con terceros y tienes control total sobre tu información.",
  },
  {
    id: "5",
    question: "¿Ofrecen período de prueba?",
    answer: "Sí, ofrecemos un período de prueba de 14 días para todos nuestros planes. Puedes explorar todas las funcionalidades sin compromiso y cancelar en cualquier momento durante el período de prueba.",
  },
  {
    id: "6",
    question: "¿Qué incluyen los servicios adicionales?",
    answer: "Nuestros servicios adicionales incluyen consultoría personalizada, desarrollo de agentes customizados, integración con sistemas específicos y capacitación para equipos. El costo es de $299/hora y se factura según el tiempo utilizado.",
  },
];

export const ADDITIONAL_SERVICE = {
  name: "Servicios Adicionales",
  price: "$299.00",
  period: "/hora",
  description: "Consultoría y personalización",
};